import React, { useState, useEffect, useRef } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { callApi, HttpMethod } from "src/services/apiService";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridRowClickEvent,
  GridRowProps,
} from "@progress/kendo-react-grid";
import {
  IExpansionMonth,
  IExpansionReportResponse,
  ExpansionBasis,
} from "src/interfaces/Reports/IExpansion";
import {
  formatCurrency,
  formatSignedCurrency,
  formatPercent,
  escCsv,
} from "./format";
import ExpansionAccountsModal from "./ExpansionAccountsModal";

// "yyyy-MM" for a given date.
const toMonthValue = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const ExpansionReport: React.FC = () => {
  const { isSysadmin, isAuthenticated } = useAppSelector(selectAuth);

  // View state is mirrored in the URL query string so a configured report can be
  // shared as a link (e.g. usage-inclusive ARR, Jan–Jun) and opens identically.
  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  // Default the end to the previous month (the last complete billing month — the
  // current month is still billing and would understate MRR), and the start to
  // January of that year.
  const priorMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const monthRe = /^\d{4}-(0[1-9]|1[0-2])$/;
  const startParam = searchParams.get("start");
  const endParam = searchParams.get("end");
  const basisParam = searchParams.get("basis");
  const unitParam = searchParams.get("unit");

  const [startMonth, setStartMonth] = useState(
    startParam && monthRe.test(startParam) ? startParam : `${priorMonth.getFullYear()}-01`,
  );
  const [endMonth, setEndMonth] = useState(
    endParam && monthRe.test(endParam) ? endParam : toMonthValue(priorMonth),
  );

  // Defaults: usage-inclusive basis + ARR (the investor-facing view).
  const [basis, setBasis] = useState<ExpansionBasis>(
    basisParam === "subscription" ? "subscription" : "usage",
  );
  const [subRows, setSubRows] = useState<IExpansionMonth[]>([]);
  const [usageRows, setUsageRows] = useState<IExpansionMonth[]>([]);
  const rows = basis === "usage" ? usageRows : subRows;

  // MRR (monthly) vs ARR (annualized = MRR x 12) — a pure display multiplier.
  const [unit, setUnit] = useState<"mrr" | "arr">(unitParam === "mrr" ? "mrr" : "arr");
  const factor = unit === "arr" ? 12 : 1;
  const unitLabel = unit === "arr" ? "ARR" : "MRR";

  // Keep the URL in sync with the current view so it can be copied and shared.
  useEffect(() => {
    setSearchParams(
      { basis, unit, start: startMonth, end: endMonth },
      { replace: true },
    );
  }, [basis, unit, startMonth, endMonth, setSearchParams]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Drill-in modal
  const [selectedMonth, setSelectedMonth] = useState<IExpansionMonth | null>(null);

  const requestIdRef = useRef(0);

  const fetchReport = async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError("");
    try {
      const [sYear, sMonth] = startMonth.split("-");
      const [eYear, eMonth] = endMonth.split("-");
      const params = new URLSearchParams({
        startMonth: String(parseInt(sMonth, 10)),
        startYear: sYear,
        endMonth: String(parseInt(eMonth, 10)),
        endYear: eYear,
      });

      const response: IExpansionReportResponse = await callApi(
        `reports/expansion?${params.toString()}`,
        HttpMethod.GET,
      );
      if (requestId !== requestIdRef.current) return;
      setSubRows(response.data || []);
      setUsageRows(response.usageInclusive || []);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(`Error loading expansion report: ${err.message || err}`);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Auth gate: sysadmin only ---
  if (isAuthenticated && isSysadmin === false) {
    return <Navigate to="/home" replace />;
  }

  // --- Handlers ---

  const handleRowClick = (event: GridRowClickEvent) => {
    const item = event.dataItem as IExpansionMonth;
    if (item.isTotal) return; // total row has no single-month drill-in
    setSelectedMonth(item);
  };

  const handleDownloadCsv = () => {
    if (rows.length === 0) return;
    const headers = [
      "Month",
      `Starting ${unitLabel}`,
      "Expansion",
      "Contraction",
      "Churned",
      "Net Expansion",
      "New",
      `Ending ${unitLabel}`,
      "NRR %",
      "# Accounts Expanded",
      "Total Invoiced",
      "TTM Invoiced",
    ];
    const body = rows.map((r) => [
      escCsv(r.monthLabel),
      String(r.startingMrr * factor),
      String(r.expansionAmount * factor),
      String(r.contractionAmount * factor),
      String(r.churnedMrr * factor),
      String(r.netExpansion * factor),
      String(r.newMrr * factor),
      String(r.endingMrr * factor),
      String(r.nrrPercent),
      String(r.accountsExpanded),
      String(r.totalInvoiced),
      String(r.ttmInvoiced),
    ]);
    const csv = [headers.join(","), ...body.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expansion-${basis}-${unit}-${startMonth}-to-${endMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Cells ---

  const CurrencyCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    return <td className="text-end">{formatCurrency((dataItem[field] ?? 0) * factor)}</td>;
  };

  // Actual billings (Total Invoiced, TTM Invoiced) — shown as-is, never annualized,
  // so they reconcile directly to billings (unlike the recurring run-rate columns
  // which the ARR toggle multiplies by 12).
  const InvoicedCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    return <td className="text-end text-muted">{formatCurrency(dataItem[field] ?? 0)}</td>;
  };

  // Colored, signed cell for expansion/net (green up, red down).
  const makeSignedCell =
    (positiveIsGood = true): React.FC<GridCellProps> =>
    (props) => {
      const { dataItem, field } = props;
      if (!field) return <td />;
      const value = dataItem[field] as number;
      const good = positiveIsGood ? value > 0 : value < 0;
      const bad = positiveIsGood ? value < 0 : value > 0;
      const cls = good ? "text-success" : bad ? "text-danger" : "";
      return <td className={`text-end fw-semibold ${cls}`}>{formatSignedCurrency(value * factor)}</td>;
    };

  const ExpansionCell = makeSignedCell(true);
  const NetCell = makeSignedCell(true);

  // Contraction/Churn are losses — show as negative in red.
  const LossCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    const value = dataItem[field] as number;
    return (
      <td className={`text-end ${value > 0 ? "text-danger" : ""}`}>
        {value > 0 ? `-${formatCurrency(value * factor)}` : formatCurrency(0)}
      </td>
    );
  };

  const NrrCell: React.FC<GridCellProps> = (props) => {
    const value = props.dataItem.nrrPercent as number;
    const cls = value >= 100 ? "text-success" : value > 0 ? "text-danger" : "text-muted";
    return <td className={`text-end fw-semibold ${cls}`}>{value > 0 ? formatPercent(value) : "—"}</td>;
  };

  const MonthCell: React.FC<GridCellProps> = (props) => {
    const item = props.dataItem as IExpansionMonth;
    return (
      <td>
        {item.isTotal ? (
          item.monthLabel
        ) : (
          <a role="button" className="text-decoration-none">
            {item.monthLabel}
          </a>
        )}
      </td>
    );
  };

  // Bold + bordered total row.
  const rowRender = (tr: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    const item = props.dataItem as IExpansionMonth;
    if (!item.isTotal) return tr;
    const extraClass = "fw-bold border-top border-2";
    const existing = (tr.props as any).className || "";
    return React.cloneElement(
      tr,
      { ...tr.props, className: `${existing} ${extraClass}` } as any,
      (tr.props as any).children,
    );
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Account Expansion</h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
            Recurring-revenue growth from existing customers (upsell + cross-sell). Click a month to
            see the accounts behind it.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleDownloadCsv}
          disabled={rows.length === 0}
        >
          <i className="bi bi-download me-1" />
          Download CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-3">
              <label className="form-label mb-1">From month</label>
              <input
                type="month"
                className="form-control"
                value={startMonth}
                max={endMonth}
                onChange={(e) => setStartMonth(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label mb-1">To month</label>
              <input
                type="month"
                className="form-control"
                value={endMonth}
                min={startMonth}
                onChange={(e) => setEndMonth(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <button className="btn btn-primary" onClick={fetchReport} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Loading...
                  </>
                ) : (
                  "Run report"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Revenue basis toggle */}
      <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
        <div className="btn-group" role="group" aria-label="Revenue basis">
          <button
            type="button"
            className={`btn btn-sm ${basis === "subscription" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setBasis("subscription")}
          >
            Subscription MRR
          </button>
          <button
            type="button"
            className={`btn btn-sm ${basis === "usage" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setBasis("usage")}
          >
            Incl. usage &amp; overage
          </button>
        </div>
        <div className="btn-group" role="group" aria-label="MRR or ARR">
          <button
            type="button"
            className={`btn btn-sm ${unit === "mrr" ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => setUnit("mrr")}
          >
            MRR
          </button>
          <button
            type="button"
            className={`btn btn-sm ${unit === "arr" ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => setUnit("arr")}
          >
            ARR
          </button>
        </div>
        <span className="text-muted" style={{ fontSize: "0.8rem" }}>
          {basis === "subscription"
            ? "Fixed recurring subscriptions only — the conservative floor."
            : "Subscription + scrub overage + subscriber usage/overage — closer to how investors count net revenue retention."}
        </span>
      </div>

      <div style={{ position: "relative" }}>
        {isLoading && (
          <div
            className="k-loading-mask"
            style={{ position: "absolute", inset: 0, zIndex: 10 }}
          >
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image" />
            <div className="k-loading-color" />
          </div>
        )}
        <Grid
          data={rows}
          sortable={false}
          onRowClick={handleRowClick}
          rowRender={rowRender}
          style={{ cursor: "pointer" }}
        >
        <Column field="monthLabel" title="Month" width={120} cell={MonthCell} />
        <Column field="startingMrr" title={`Starting ${unitLabel}`} cell={CurrencyCell} />
        <Column field="expansionAmount" title="Expansion" cell={ExpansionCell} />
        <Column field="contractionAmount" title="Contraction" cell={LossCell} />
        <Column field="churnedMrr" title="Churned" cell={LossCell} />
        <Column field="netExpansion" title="Net Expansion" cell={NetCell} />
        <Column field="newMrr" title="New" cell={CurrencyCell} />
        <Column field="endingMrr" title={`Ending ${unitLabel}`} cell={CurrencyCell} />
        <Column field="nrrPercent" title="NRR" width={100} cell={NrrCell} />
        <Column field="accountsExpanded" title="# Expanded" width={110} />
        <Column field="totalInvoiced" title="Total Invoiced" cell={InvoicedCell} />
        <Column field="ttmInvoiced" title="TTM Invoiced" cell={InvoicedCell} />
        </Grid>
      </div>

      <p className="text-muted mt-3" style={{ fontSize: "0.8rem" }}>
        Two revenue bases are available. <strong>Subscription MRR</strong> counts fixed recurring
        subscriptions only and excludes one-time/setup fees, overages, and usage-based charges (e.g.
        per-branded-call Branding usage, scrub overages) — a conservative floor. <strong>Incl. usage
        &amp; overage</strong> adds scrub overage and per-product usage/overage for accounts that
        subscribe to that product (excluding only genuine one-time/setup), which is closer to the net
        revenue retention investors expect for usage-priced products. In both, multiple invoices per
        account per month are collapsed, late-billed invoices are attributed to their intended month,
        annual/prepaid contracts are spread to a flat monthly run-rate (no one-month spike), and
        new-logo revenue is excluded from expansion.
      </p>

      {selectedMonth && (
        <ExpansionAccountsModal
          month={selectedMonth.month}
          monthLabel={selectedMonth.monthLabel}
          basis={basis}
          factor={factor}
          unitLabel={unitLabel}
          onClose={() => setSelectedMonth(null)}
        />
      )}
    </div>
  );
};

export default ExpansionReport;
